import { mergeSx, SxProps, tokens } from '@cast/design-system';

import { HEADER_HEIGHT } from '../../layoutConstants';

const SIDEBAR_WIDTH = 236;

/** Figma row vertical padding — no matching primitive token */
const sidebarItemPaddingY = '5px';

export const sidebarItemGap = tokens.primitives.space['200'];
export const sidebarNestedGap = '6px';
const sidebarSectionGap = tokens.primitives.space['100'];

const sidebarTreeIndent = `calc(${tokens.primitives.space['300']} + ${tokens.primitives.space['200']})`;

const sidebarContentPadding = `${tokens.primitives.space['400']} ${tokens.primitives.space['300']}`;

export const sidebarContainerSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  width: SIDEBAR_WIDTH,
  minWidth: SIDEBAR_WIDTH,
  height: `calc(100dvh - ${HEADER_HEIGHT}px)`,
  position: 'sticky',
  top: `${HEADER_HEIGHT}px`,
  backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
  borderRight: `1px solid ${tokens.theme.border.structural.neutral}`,
  zIndex: 1,
};

/** Scrollport — no padding so overlay scrollbar is flush with sidebar edges. */
export const sidebarNavSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflowX: 'hidden',
  overflowY: 'auto',
  // Thin thumb on hover only — avoids a busy bar when content barely overflows.
  scrollbarWidth: 'thin',
  scrollbarColor: 'transparent transparent',
  '&:hover': {
    scrollbarColor: `${tokens.theme.border.structural.neutral} transparent`,
  },
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'transparent',
    borderRadius: '3px',
  },
  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor: tokens.theme.border.structural.neutral,
  },
};

export const sidebarNavContentSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: sidebarSectionGap,
  padding: sidebarContentPadding,
};

export const sidebarToolboxSx: SxProps = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  px: tokens.primitives.space['300'],
  pb: tokens.primitives.space['400'],
  pt: tokens.primitives.space['200'],
  mt: 'auto',
  flexShrink: 0,
};

export const sidebarItemListSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: sidebarSectionGap,
};

export const sidebarSectionLabelSx: SxProps = {
  px: tokens.primitives.space['300'],
  py: tokens.primitives.space['100'],
  color: tokens.theme.fg.structural.secondary,
  letterSpacing: '1px',
  textTransform: 'uppercase',
};

export type SidebarItemVisualState = {
  active?: boolean;
  /** Parent with an active child: selection color, no pill. */
  indicated?: boolean;
};

/** `active` = leaf pill; `indicated` = parent text color only. */
export const getSidebarItemColors = ({
  active = false,
  indicated = false,
}: SidebarItemVisualState) => {
  if (active) {
    return {
      backgroundColor: tokens.theme.bg.selection.primarySubtle,
      color: tokens.theme.fg.selection.primary,
    };
  }

  if (indicated) {
    return {
      backgroundColor: 'transparent',
      color: tokens.theme.fg.selection.primary,
    };
  }

  return {
    backgroundColor: 'transparent',
    color: tokens.theme.fg.structural.secondary,
  };
};

const sidebarItemRowSx = (
  state: SidebarItemVisualState & { isNested?: boolean }
): SxProps => {
  const colors = getSidebarItemColors(state);

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: '32px',
    height: '32px',
    borderRadius: '4px',
    cursor: 'pointer',
    textDecoration: 'none',
    color: colors.color,
    backgroundColor: colors.backgroundColor,
    padding: state.isNested
      ? tokens.primitives.space['100']
      : `${sidebarItemPaddingY} ${tokens.primitives.space['200']} ${sidebarItemPaddingY} ${tokens.primitives.space['300']}`,
    transition: 'background-color 120ms ease, color 120ms ease',

    '&:hover': {
      backgroundColor: state.active
        ? tokens.theme.bg.selection.primarySubtle
        : tokens.theme.bg.action.ghostHover,
    },
  };
};

const sidebarItemButtonResetSx: SxProps = {
  border: 'none',
  font: 'inherit',
  textAlign: 'left',
};

export const sidebarItemLinkSx: SxProps = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
};

export const sidebarDividerWrapperSx: SxProps = {
  py: tokens.primitives.space['200'],
  width: '100%',
};

export const sidebarTreeGroupSx: SxProps = {
  display: 'flex',
  alignItems: 'stretch',
  width: '100%',
};

export const sidebarTreeLineSx: SxProps = {
  display: 'flex',
  pl: sidebarTreeIndent,
  pr: tokens.primitives.space['300'],
  flexShrink: 0,
};

export const sidebarTreeChildrenSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: sidebarSectionGap,
  flex: 1,
  minWidth: 0,
};

export const mergeSidebarItemSx = (
  state: SidebarItemVisualState & { isNested?: boolean },
  sx?: SxProps
) => mergeSx(sidebarItemRowSx(state), sidebarItemButtonResetSx, sx);
